/**
 * @description
 * Deeply compares two objects and returns true if they are equal, false
 * otherwise.
 *
 * Used as a drop-in replacement for lodash's isEqual function, in order to
 * reduce bundle size.
 *
 * @param {Object} obj1 - The first object to compare
 * @param {Object} obj2 - The second object to compare
 * @returns {boolean} - True if the objects are equal, false otherwise
 **/
export default function deepEqual(obj1, obj2) {
  if (obj1 === obj2) {
    return true;
  }

  // Check if both are null or undefined, but not one of them
  if (
    (obj1 === null || obj1 === undefined) &&
    obj2 !== null &&
    obj2 !== undefined
  ) {
    return false;
  }

  if (
    (obj2 === null || obj2 === undefined) &&
    obj1 !== null &&
    obj1 !== undefined
  ) {
    return false;
  }

  // Check if both are instances of the Date object and have the same value
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }

  // Check if one of them is a Date and the other isn't
  if (
    (obj1 instanceof Date && !(obj2 instanceof Date)) ||
    (!(obj1 instanceof Date) && obj2 instanceof Date)
  ) {
    return false;
  }

  // Check if both are arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      return false;
    }
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) {
        return false;
      }
    }
    return true;
  }

  // Check if one is an array and the other isn't
  if (
    (Array.isArray(obj1) && !Array.isArray(obj2)) ||
    (!Array.isArray(obj1) && Array.isArray(obj2))
  ) {
    return false;
  }

  // If both are objects
  if (typeof obj1 === 'object' && typeof obj2 === 'object') {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (let key of keys1) {
      if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  }

  return false;
}
